<template>
  <div class="strategy-list-page" :class="{ 'is-mobile': isMobileRoute }">
    <div class="strategy-list-shell">
      <nav v-if="isMobileRoute" class="mobile-category-tabs" aria-label="攻略分类">
        <button
          v-for="category in categories"
          :key="category.id"
          type="button"
          class="mobile-category-tab"
          :class="{ active: String(category.id) === String(categoryId) }"
          @click="handleMobileCategoryClick(category)"
        >
          {{ category.name }}
        </button>
      </nav>

      <div v-if="!isMobileRoute" class="breadcrumb">
        <span @click="goToHome" class="breadcrumb-home">攻略</span>
        <span class="breadcrumb-separator">&gt;</span>
        <span class="breadcrumb-current">{{ currentCategory.title }}</span>
      </div>

      <section class="list-grid">
        <article
          v-if="isPageLoading"
          v-for="index in 10"
          :key="`strategy-list-skeleton-${index}`"
          class="list-card list-card-skeleton"
          aria-hidden="true"
        >
          <template v-if="isMobileRoute">
            <div class="mobile-list-main">
              <div class="mobile-skeleton-line title"></div>
              <div class="mobile-skeleton-line meta"></div>
              <div class="mobile-skeleton-line desc"></div>
            </div>
            <div class="mobile-list-thumb mobile-list-thumb-skeleton">
              <div class="list-card-cover-loading"></div>
            </div>
          </template>

          <template v-else>
            <div class="list-card-cover">
              <div class="list-card-cover-loading"></div>
            </div>
          </template>
        </article>

        <article
          v-else
          v-for="article in strategyDataList"
          :key="article.id"
          class="list-card"
          @click="goToStrategyDetail(article)"
        >
          <template v-if="isMobileRoute">
            <div class="mobile-list-main">
              <div class="mobile-list-kicker">
                <span class="mobile-topic-icon" :class="getCurrentCategoryIconClass()" aria-hidden="true"></span>
                <span>{{ article.title }}</span>
              </div>
              <div class="mobile-author">
                <span class="mobile-author-avatar">{{ getAuthorInitial(article) }}</span>
                <span>{{ article.nickName || '化化平台官方' }}</span>
              </div>
              <p class="mobile-list-desc">{{ getArticleSummary(article) }}</p>
            </div>
            <div class="mobile-list-thumb">
              <img
                :src="article?.coverImg?.url || fallbackCover"
                :alt="article.title"
                loading="lazy"
                @error="handleCoverError($event, article)"
              >
            </div>
          </template>

          <template v-else>
            <div class="list-card-cover">
              <div v-if="!isCoverLoaded(article)" class="list-card-cover-loading"></div>
              <img
                :src="article?.coverImg?.url || fallbackCover"
                :alt="article.title"
                loading="lazy"
                :class="{ loaded: isCoverLoaded(article) }"
                @load="markCoverLoaded(article)"
                @error="handleCoverError($event, article)"
              >
            </div>
            <h3 class="list-card-title">{{ article.title }}</h3>
          </template>
        </article>
      </section>

      <div class="pagination-bar" v-if="pageCount > 1 && !isMobileRoute && !isPageLoading">
        <button
          type="button"
          class="page-btn nav-btn"
          :disabled="currentPage === 1"
          @click="changePage(currentPage - 1)"
        >
          上一页
        </button>

        <button
          v-for="page in pageCount"
          :key="page"
          type="button"
          class="page-btn number-btn"
          :class="{ active: currentPage === page }"
          @click="changePage(page)"
        >
          {{ page }}
        </button>

        <button
          type="button"
          class="page-btn nav-btn"
          :disabled="currentPage === pageCount"
          @click="changePage(currentPage + 1)"
        >
          下一页
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { getStrategyCategoryPageList, getStrategyPageList } from '@/api/strategy'
import {
  STRATEGY_LIST_DEFAULT_TITLE,
  getStrategyCategoryChildIds,
  getStrategyCategoryTitle,
  getStrategyListSeoTitle
} from '@/utils/seo-route-data'
import { getPageSsgState, setPageSsgState, shouldMarkImageLoaded } from '@/utils/ssg-state'

const PAGE_SIZE = 30
const SSG_STATE_KEY = 'strategyListPage'

export default {
  name: 'StrategyListPage',
  setup() {
    const route = useRoute()
    const categoryId = route.params.categoryId || route.query.categoryId
    const pageTitle = ref(getStrategyListSeoTitle(categoryId))

    useHead(() => ({
      title: pageTitle.value,
      meta: [
        { name: 'description', content: '化化网装修与自建房攻略，提供预算规划、合同签署、硬装施工、软装搭配、选材指南、验收入住等实用干货，业主必看的装修与自建房知识库。' },
        { name: 'keywords', content: '装修攻略,自建房攻略,装修避坑,装修知识,自建房避坑,自建房知识,化化网' }
      ]
    }))

    return {
      pageTitle
    }
  },
  data() {
    return {
      currentPage: 1,
      pageSize: PAGE_SIZE,
      currentCategoryTitle: '',
      categoryId: null,
      childCategoryIds: [],
      categories: [],
      strategyDataList: [],
      count: 0,
      coverLoadedMap: {},
      isPageLoading: typeof window !== 'undefined',
      fallbackCover: 'https://obs.huahuaeco.com/official/product2.png'
    }
  },
  computed: {
    isMobileRoute() {
      return this.$route.path === '/m/strategy/list' || this.$route.path.startsWith('/m/strategy/list/')
    },
    currentCategory() {
      return { title: this.currentCategoryTitle }
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.count / this.pageSize))
    }
  },
  watch: {
    '$route.query': {
      handler() {
        this.refreshRouteData()
      },
      deep: true
    },
    '$route.params': {
      handler() {
        this.refreshRouteData()
      },
      deep: true
    }
  },
  created() {
    this.applySsgState()
  },
  mounted() {
    this.refreshRouteData()
  },
  serverPrefetch() {
    return this.syncRouteData()
  },
  methods: {
    applySsgState() {
      const state = getPageSsgState(this.$route, SSG_STATE_KEY)
      if (!state) {
        return
      }

      this.currentPage = state.currentPage || 1
      this.pageSize = state.pageSize || PAGE_SIZE
      this.currentCategoryTitle = state.currentCategoryTitle || ''
      this.categoryId = state.categoryId || null
      this.childCategoryIds = Array.isArray(state.childCategoryIds) ? state.childCategoryIds : []
      this.categories = Array.isArray(state.categories) ? state.categories : []
      this.strategyDataList = Array.isArray(state.strategyDataList) ? state.strategyDataList : []
      this.count = state.count || 0
      this.coverLoadedMap = state.coverLoadedMap || {}
      if (state.pageTitle) {
        this.pageTitle = state.pageTitle
      }
    },
    persistSsgState() {
      setPageSsgState(this.$route, SSG_STATE_KEY, {
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        currentCategoryTitle: this.currentCategoryTitle,
        categoryId: this.categoryId,
        childCategoryIds: this.childCategoryIds,
        categories: this.categories,
        strategyDataList: this.strategyDataList,
        count: this.count,
        coverLoadedMap: this.coverLoadedMap,
        pageTitle: this.pageTitle
      })
    },
    withMobilePrefix(path) {
      return this.isMobileRoute ? `/m${path}` : path
    },
    goToHome() {
      this.$router.push({ path: this.withMobilePrefix('/strategy') })
    },
    async syncRouteData() {
      const { categoryId: queryCategoryId, childCategoryIds } = this.$route.query
      this.categoryId = this.$route.params.categoryId || queryCategoryId || ''
      if (this.isMobileRoute) {
        await this.ensureMobileCategories()
      }
      this.childCategoryIds = childCategoryIds
        ? childCategoryIds.split(',').filter(Boolean)
        : getStrategyCategoryChildIds(this.categoryId)

      if (this.isMobileRoute && !this.categoryId && this.categories.length) {
        const firstCategory = this.categories[0]
        this.categoryId = firstCategory.id
        this.currentCategoryTitle = firstCategory.name || ''
        this.childCategoryIds = await this.fetchCategoryChildrenIds(firstCategory.id)
      }

      this.currentPage = 1
      await this.syncCurrentCategoryTitle()
      if (this.isMobileRoute && this.categoryId && !this.childCategoryIds.length) {
        this.childCategoryIds = await this.fetchCategoryChildrenIds(this.categoryId)
      }
      this.pageTitle = this.currentCategoryTitle
        ? `${this.currentCategoryTitle} - 装修攻略_自建房攻略-化化网`
        : STRATEGY_LIST_DEFAULT_TITLE
      await this.getStrategyPageListApi()
    },
    async refreshRouteData() {
      this.isPageLoading = true
      try {
        await this.syncRouteData()
      } finally {
        this.isPageLoading = false
      }
    },
    async ensureMobileCategories() {
      if (this.categories.length) {
        return
      }

      let categories = []
      if (typeof localStorage !== 'undefined') {
        categories = JSON.parse(localStorage.getItem('strategyCategories') || '[]')
      }

      if (!categories.length) {
        try {
          const res = await getStrategyCategoryPageList({
            categoryId: 0,
            isExtension: true,
            pageNum: 1,
            pageSize: 100
          })
          categories = res?.data?.data || []
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('strategyCategories', JSON.stringify(categories))
          }
        } catch (error) {
          console.error('Failed to fetch strategy categories:', error)
        }
      }

      this.categories = categories
    },
    async fetchCategoryChildrenIds(categoryId) {
      if (!categoryId) {
        return []
      }

      const currentCategory = this.categories.find((item) => String(item.id) === String(categoryId))
      if (Array.isArray(currentCategory?.listModel) && currentCategory.listModel.length) {
        return currentCategory.listModel.map((item) => item.id).filter(Boolean)
      }

      const cachedChildIds = getStrategyCategoryChildIds(categoryId)
      if (cachedChildIds.length) {
        return cachedChildIds
      }

      try {
        const res = await getStrategyCategoryPageList({
          categoryId,
          isExtension: true,
          pageNum: 1,
          pageSize: 100
        })
        return (res?.data?.data || []).map((item) => item.id).filter(Boolean)
      } catch (error) {
        console.error('Failed to fetch strategy child categories:', error)
        return []
      }
    },
    async syncCurrentCategoryTitle() {
      const cachedCategoryTitle = getStrategyCategoryTitle(this.categoryId)
      if (cachedCategoryTitle) {
        this.currentCategoryTitle = cachedCategoryTitle
        return
      }

      let categories = []
      if (typeof localStorage !== 'undefined') {
        categories = JSON.parse(localStorage.getItem('strategyCategories') || '[]')
      }

      if (!categories.length) {
        try {
          const res = await getStrategyCategoryPageList({
            categoryId: 0,
            isExtension: true,
            pageNum: 1,
            pageSize: 100
          })
          categories = res?.data?.data || []
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('strategyCategories', JSON.stringify(categories))
          }
        } catch (error) {
          console.error('Failed to fetch strategy categories:', error)
        }
      }

      const currentCategory = categories.find((item) => String(item.id) === String(this.categoryId))
      this.currentCategoryTitle = currentCategory?.name || ''
      if (!this.childCategoryIds.length && Array.isArray(currentCategory?.listModel)) {
        this.childCategoryIds = currentCategory.listModel.map((item) => item.id).filter(Boolean)
      }
    },
    async getStrategyPageListApi() {
      const categoryIdList = [this.categoryId, ...this.childCategoryIds].filter(Boolean)
      try {
        const res = await getStrategyPageList({
          categoryIdList,
          pageNum: this.currentPage,
          pageSize: this.pageSize
        })
         const { data = [], total = 0 } = res.data || {}
         this.strategyDataList = data.map((item) => ({
           ...item,
           childCategoryId: item.childCategoryId || this.childCategoryIds[0] || ''
         }))
         this.coverLoadedMap = this.strategyDataList.reduce((result, item) => {
           result[item.id] = shouldMarkImageLoaded(item?.coverImg?.url)
           return result
         }, {})
         this.count = total
         this.persistSsgState()
      } catch (error) {
        console.error('Failed to fetch related strategies:', error)
      }
    },
    isCoverLoaded(article) {
      if (!article?.id) {
        return true
      }

      return Boolean(this.coverLoadedMap[article.id])
    },
    markCoverLoaded(article) {
      if (!article?.id) {
        return
      }

      this.coverLoadedMap[article.id] = true
    },
    handleCoverError(event, article) {
      const target = event?.target
      if (target && target.getAttribute('src') !== this.fallbackCover) {
        if (article) {
          article.coverImg = {
            ...(article.coverImg || {}),
            url: this.fallbackCover
          }
        }
        target.src = this.fallbackCover
        return
      }

      if (target) {
        target.style.visibility = 'hidden'
      }
      this.markCoverLoaded(article)
    },
    stripHtml(value = '') {
      return String(value).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
    },
    getArticleSummary(article) {
      const text = this.stripHtml(article?.summary || article?.description || article?.content || '')
      return text.length > 46 ? `${text.slice(0, 46)}...` : text
    },
    getCurrentCategoryIconClass() {
      const index = this.categories.findIndex((category) => String(category.id) === String(this.categoryId))
      return `icon-${(index >= 0 ? index : 0) % 5}`
    },
    getAuthorInitial(article) {
      const name = article?.nickName || '化'
      return String(name).trim().slice(0, 1) || '化'
    },
    async handleMobileCategoryClick(category) {
      if (!category || String(category.id) === String(this.categoryId)) {
        return
      }

      const childCategoryIds = await this.fetchCategoryChildrenIds(category.id)
      this.$router.push({
        path: `/m/strategy/list/${category.id}`,
        query: childCategoryIds.length ? { childCategoryIds: childCategoryIds.join(',') } : {}
      })
    },
    changePage(page) {
      if (page < 1 || page > this.pageCount || page === this.currentPage) return
      this.currentPage = page
      this.getStrategyPageListApi()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    goToStrategyDetail(article) {
      const articleId = article.id || article.title
      this.$router.push({
        path: this.withMobilePrefix(`/strategy/detail/${articleId}`),
        query: {
          categoryId: this.categoryId,
          childCategoryId: article.childCategoryId || this.childCategoryIds[0] || ''
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.strategy-list-page {
  padding: 92px 0 80px;
  background: #f3f4f7;
}

.strategy-list-shell {
  width: min(1200px, calc(100% - 48px));
  margin: 0 auto;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 18px;
  font-size: 12px;
  line-height: 1.4;
  color: #7a7a7a;
}
.breadcrumb-home {
  cursor: pointer;
}
.breadcrumb-current {
  color: #434343;
}

.list-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 26px 14px;
}

.list-card {
  min-width: 0;
  cursor: pointer;
}

.list-card-skeleton {
  cursor: default;
}

.list-card-cover {
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  background: #f5f1eb;
}

.list-card-skeleton .list-card-cover {
  aspect-ratio: 1 / 0.78;
}

.list-card-cover-loading {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.5) 50%, rgba(255, 255, 255, 0) 100%), #efe7dd;
  background-size: 200% 100%;
  animation: list-card-shimmer 1.2s ease-in-out infinite;
}

.list-card-cover img {
  display: block;
  width: 100%;
  aspect-ratio: 1 / 0.78;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.24s ease;
}

.list-card-cover img.loaded {
  opacity: 1;
}

.list-card-title {
  margin: 8px 0 0;
  color: #222;
  font-size: 12px;
  line-height: 1.5;
  font-weight: 500;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mobile-list-thumb-skeleton {
  position: relative;
}

.mobile-skeleton-line {
  height: 16px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.55) 50%, rgba(255, 255, 255, 0) 100%), #efe7dd;
  background-size: 200% 100%;
  animation: list-card-shimmer 1.2s ease-in-out infinite;
}

.mobile-skeleton-line.title {
  width: 78%;
}

.mobile-skeleton-line.meta {
  width: 46%;
  margin-top: 14px;
}

.mobile-skeleton-line.desc {
  width: 92%;
  margin-top: 16px;
}

.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 36px;
}

.page-btn {
  min-width: 34px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #333;
  font-size: 12px;
  line-height: 26px;
  cursor: pointer;
}

.page-btn.active {
  border-color: #1677ff;
  background: #1677ff;
  color: #fff;
}

.page-btn:disabled {
  color: #bfbfbf;
  cursor: not-allowed;
  background: #fafafa;
}

@keyframes list-card-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

.strategy-list-page.is-mobile {
  min-height: 100vh;
  padding: calc(88 * var(--rpx)) 0 calc(40 * var(--rpx));
  background: #f4f7fb;

  .strategy-list-shell {
    width: 100%;
    margin: 0;
  }

  .mobile-category-tabs {
    position: sticky;
    top: calc(88 * var(--rpx));
    z-index: 20;
    display: flex;
    gap: calc(30 * var(--rpx));
    overflow-x: auto;
    padding: calc(18 * var(--rpx)) calc(24 * var(--rpx)) calc(14 * var(--rpx));
    background: #fff;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .mobile-category-tab {
    flex: 0 0 auto;
    padding: 0;
    border: 0;
    background: transparent;
    color: #595959;
    font-size: calc(26 * var(--rpx));
    font-weight: 500;
    line-height: calc(46 * var(--rpx));
    white-space: nowrap;
  }

  .mobile-category-tab.active {
    color: #262626;
    font-size: calc(30 * var(--rpx));
    font-weight: 700;
  }

  .list-grid {
    display: flex;
    flex-direction: column;
    gap: calc(16 * var(--rpx));
    padding: calc(18 * var(--rpx)) calc(18 * var(--rpx)) 0;
  }

  .list-card {
    display: flex;
    gap: calc(18 * var(--rpx));
    align-items: stretch;
    min-height: calc(184 * var(--rpx));
    padding: calc(18 * var(--rpx));
    border-radius: calc(14 * var(--rpx));
    background: #fff;
    box-shadow: 0 calc(10 * var(--rpx)) calc(26 * var(--rpx)) rgba(56, 89, 126, 0.06);
  }

  .mobile-list-main {
    flex: 1;
    min-width: 0;
  }

  .mobile-list-kicker {
    display: flex;
    align-items: center;
    gap: calc(8 * var(--rpx));
    color: #262626;
    font-size: calc(24 * var(--rpx));
    font-weight: 700;
    line-height: 1.35;

    span:last-child {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .mobile-topic-icon {
    width: calc(24 * var(--rpx));
    height: calc(24 * var(--rpx));
    border-radius: 50%;
    position: relative;
    flex: 0 0 auto;
    background: #20c77b;

    &::after {
      content: "";
      position: absolute;
      left: calc(9 * var(--rpx));
      top: calc(6 * var(--rpx));
      width: 0;
      height: 0;
      border-top: calc(6 * var(--rpx)) solid transparent;
      border-bottom: calc(6 * var(--rpx)) solid transparent;
      border-left: calc(8 * var(--rpx)) solid #fff;
    }
  }

  .mobile-topic-icon.icon-1 {
    border-radius: calc(6 * var(--rpx));
    background: linear-gradient(135deg, #49a5ff, #198cfe);

    &::after {
      left: calc(6 * var(--rpx));
      top: calc(5 * var(--rpx));
      width: calc(12 * var(--rpx));
      height: calc(12 * var(--rpx));
      border: calc(3 * var(--rpx)) solid #fff;
      border-top: 0;
      border-radius: 0 0 calc(3 * var(--rpx)) calc(3 * var(--rpx));
      transform: rotate(45deg);
    }
  }

  .mobile-topic-icon.icon-2 {
    background: linear-gradient(135deg, #ffb45c, #ff7a45);

    &::after {
      left: calc(6 * var(--rpx));
      top: calc(7 * var(--rpx));
      width: calc(12 * var(--rpx));
      height: calc(7 * var(--rpx));
      border: 0;
      border-left: calc(3 * var(--rpx)) solid #fff;
      border-bottom: calc(3 * var(--rpx)) solid #fff;
      transform: rotate(-45deg);
    }
  }

  .mobile-topic-icon.icon-3 {
    border-radius: calc(8 * var(--rpx));
    background: linear-gradient(135deg, #8b7cff, #5d6bff);

    &::before,
    &::after {
      content: "";
      position: absolute;
      left: calc(6 * var(--rpx));
      right: calc(6 * var(--rpx));
      height: calc(3 * var(--rpx));
      border-radius: calc(999 * var(--rpx));
      background: #fff;
    }

    &::before {
      top: calc(7 * var(--rpx));
    }

    &::after {
      top: calc(14 * var(--rpx));
      width: calc(8 * var(--rpx));
      right: auto;
      border: 0;
    }
  }

  .mobile-topic-icon.icon-4 {
    background: linear-gradient(135deg, #45d6d8, #18a8c7);

    &::after {
      left: calc(7 * var(--rpx));
      top: calc(5 * var(--rpx));
      width: calc(10 * var(--rpx));
      height: calc(14 * var(--rpx));
      border: calc(3 * var(--rpx)) solid #fff;
      border-radius: calc(999 * var(--rpx));
    }
  }

  .mobile-author {
    display: flex;
    align-items: center;
    gap: calc(8 * var(--rpx));
    margin-top: calc(10 * var(--rpx));
    color: #8c8c8c;
    font-size: calc(20 * var(--rpx));
    line-height: 1.4;
  }

  .mobile-author-avatar {
    width: calc(30 * var(--rpx));
    height: calc(30 * var(--rpx));
    border-radius: 50%;
    color: #fff;
    text-align: center;
    font-size: calc(18 * var(--rpx));
    line-height: calc(30 * var(--rpx));
    background: linear-gradient(135deg, #5bf7fe, #198cfe);
  }

  .mobile-list-desc {
    margin: calc(12 * var(--rpx)) 0 0;
    color: #8c8c8c;
    font-size: calc(20 * var(--rpx));
    line-height: 1.5;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .mobile-list-thumb {
    flex: 0 0 calc(142 * var(--rpx));
    width: calc(142 * var(--rpx));
    border-radius: calc(8 * var(--rpx));
    overflow: hidden;
    background: #f0ece6;

    img {
      display: block;
      width: 100%;
      height: 100%;
      min-height: calc(142 * var(--rpx));
      object-fit: cover;
    }
  }
}

@media (max-width: 1200px) {
  .list-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .strategy-list-page {
    padding: 84px 0 56px;
  }

  .strategy-list-shell {
    width: min(100%, calc(100% - 32px));
  }

  .list-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .list-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px 12px;
  }

  .pagination-bar {
    flex-wrap: wrap;
  }
}
</style>
